"use client"

import { useState, useRef } from "react"
import { FiUpload, FiFile, FiImage, FiX } from "react-icons/fi"

const DocumentUpload = ({ moveId, onUploadSuccess }) => {
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files)
    setFiles((prev) => [
      ...prev,
      ...selectedFiles.map((file) => ({
        file,
        id: Date.now() + Math.random(),
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    ])
  }

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const uploadFiles = async () => {
    if (files.length === 0) return

    setUploading(true)
    try {
      const formData = new FormData()
      files.forEach(({ file }) => {
        formData.append("files", file)
      })
      formData.append("moveId", moveId)

      // Simulate upload - replace with actual API call
      await new Promise((resolve) => setTimeout(resolve, 2000))

      setFiles([])
      onUploadSuccess?.()
    } catch (error) {
      console.error("Upload failed:", error)
    } finally {
      setUploading(false)
    }
  }

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return <FiImage className="w-5 h-5" />
    return <FiFile className="w-5 h-5" />
  }

  return (
    <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700/50 overflow-hidden">
      <div className="px-6 py-5 bg-gradient-to-r from-amber-500/10 to-amber-400/10 border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <FiUpload className="w-5 h-5 text-amber-400" />
          <div className="text-lg font-semibold text-white">Documents et photos</div>
        </div>
      </div>

      <div className="p-6">
        {/* Upload Area */}
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-amber-400/50 hover:bg-amber-400/5 transition-all duration-200"
        >
          <FiUpload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-300 font-medium mb-2">Cliquez pour ajouter des fichiers</p>
          <p className="text-gray-500 text-sm">Photos, PDF, documents (max 10MB par fichier)</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-6 space-y-3">
            <h4 className="text-white font-medium">Fichiers sélectionnés ({files.length})</h4>
            {files.map(({ id, file, preview }) => (
              <div key={id} className="flex items-center gap-3 p-3 bg-gray-700/30 rounded-lg">
                {preview ? (
                  <img src={preview || "/placeholder.svg"} alt={file.name} className="w-10 h-10 object-cover rounded" />
                ) : (
                  <div className="w-10 h-10 bg-gray-600 rounded flex items-center justify-center">
                    {getFileIcon(file)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{file.name}</p>
                  <p className="text-gray-400 text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button onClick={() => removeFile(id)} className="text-red-400 hover:text-red-300 transition-colors">
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            ))}

            <button
              onClick={uploadFiles}
              disabled={uploading}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Téléchargement...
                </>
              ) : (
                <>
                  <FiUpload className="w-4 h-4" />
                  Télécharger {files.length} fichier{files.length > 1 ? "s" : ""}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentUpload
